// İSG Portal — İzne araç ekleme çekmecesi (api-contract SP §23.4 `POST /supplier/permits/{uuid}/vehicles`,
// EP §23.3 `POST /permits/{uuid}/vehicles`). SP ve EP kopyaları GL12'de birleşti; uç, kayıt listesi ve
// önbellek portala göre `VehicleClient`'tan gelir.
// İzin aracı KOPYALAMAZ, BAĞLAR: taşeronun araç kaydından çoklu seçim + (isteğe bağlı) yeni plaka.
// Uç tek araçlıdır → seçilenler SIRAYLA bağlanır, ilk hatada durulur (`linkVehiclesSequentially`);
// bağlar idempotenttir, yeniden denemek güvenlidir. Yalnız sekme araç çubuğundan açılır, `VehicleSheet`
// içinden asla. Kayıt listesi kayıt ekranıyla (SP `Araçlar` sayfası, EP taşeron `Araçlar` sekmesi) AYNI
// önbellek girdisidir (`client.registryKey`); listeleme yetkisi yoksa liste hatası gösterilir ama yeni
// plaka girişi çalışır.
//
// KURAL: `--z-popover` katmanı tokens.css'te tanımlanana kadar bu çekmecenin İÇİNE `RowActionsMenu`,
// `SelectMenu` veya `Tooltip` konmaz — çekmecenin altında kalırlar. Vazgeçme onayı `Modal` olarak kalır.

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { VehicleWriteRequest } from '../api/permits';
import { resolveApiErrorMessage } from '../i18n/apiError';
import {
  VehicleLinkBatchError,
  batchErrorFullyMapped,
  descriptionTooLong,
  displayPlate,
  linkVehiclesSequentially,
  plateIssue,
  plateIssueMessage,
  plateKey,
  remainingSelection,
  vehicleDescriptionTooLongMessage,
  vehicleFieldErrorMessage,
  vehicleFieldErrors,
} from '../permits/vehicleRegistry';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Field } from '../ui/Field';
import { FormSection } from '../ui/FormSection';
import { Input } from '../ui/Input';
import { Sheet } from '../ui/Sheet';
import { Spinner } from '../ui/Spinner';
import { Textarea } from '../ui/Textarea';
import { useDiscardGuard } from '../ui/useDiscardGuard';
import type { VehicleClient } from './VehicleClient';

export interface PermitVehicleAddSheetProps {
  client: VehicleClient;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  permitUuid: string;
  /** Zaten bağlı araçların kayıt uuid'leri — seçenekten çıkarılır. */
  linkedUuids: string[];
  /** Açan araç çubuğu düğmesi — kapanışta odak buraya döner. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}

export function PermitVehicleAddSheet({
  client,
  open,
  onOpenChange,
  permitUuid,
  linkedUuids,
  restoreFocusRef,
}: PermitVehicleAddSheetProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const inflightRef = useRef(false);

  // Paylaşılan anahtar: kayıt ekranıyla AYNI önbellek girdisi (panel mount'ta ısıtır).
  const registry = useQuery({
    queryKey: client.registryKey,
    queryFn: client.listRegistry,
    enabled: client.registryEnabled,
    staleTime: 60_000,
  });
  const linked = useMemo(() => new Set(linkedUuids), [linkedUuids]);
  const options = useMemo(
    () => (registry.data ?? []).filter((v) => !linked.has(v.uuid)),
    [registry.data, linked],
  );

  const schema = useMemo(
    () =>
      z.object({
        // Boş plaka serbest (yalnız kayıttan seçim); girilmişse anahtar (harf+rakam) boş olamaz, ≤ 20.
        plate: z
          .string()
          .transform(displayPlate)
          .pipe(
            z.string().superRefine((p, ctx) => {
              const issue = plateIssue(p, true);
              if (issue) {
                ctx.addIssue({ code: 'custom', message: plateIssueMessage(issue, t) });
              }
            }),
          ),
        description: z
          .string()
          .trim()
          .refine((d) => !descriptionTooLong(d), vehicleDescriptionTooLongMessage(t)),
      }),
    [t],
  );
  type FormValues = z.input<typeof schema>;
  type Parsed = z.output<typeof schema>;

  const { register, handleSubmit, watch, reset, setError, setValue, clearErrors, formState } =
    useForm<FormValues, unknown, Parsed>({
      resolver: zodResolver(schema),
      defaultValues: { plate: '', description: '' },
    });
  const typedPlate = watch('plate');
  const typedKey = plateKey(typedPlate);
  // Yalnız gösterim ipucu — kararı sunucu verir.
  const matchedOption = typedKey ? options.find((v) => plateKey(v.plate) === typedKey) : undefined;
  const matchedOnPermit = typedKey
    ? (registry.data ?? []).find((v) => linked.has(v.uuid) && plateKey(v.plate) === typedKey)
    : undefined;
  // Açıklama yalnız gerçekten YENİ bir plaka için anlamlı (var olan araçta sunucu yok sayar).
  const descriptionVisible = typedKey.length > 0 && !matchedOption && !matchedOnPermit;
  // Açıklama gizlenince değeri temizlenir: gizli >500 karakterlik bir değer gönderimi sessizce
  // engellemesin ve `isDirty`yi açık tutmasın.
  const descriptionValue = watch('description');
  useEffect(() => {
    if (!descriptionVisible && descriptionValue !== '') {
      setValue('description', '', { shouldDirty: true });
      clearErrors('description');
    }
  }, [descriptionVisible, descriptionValue, setValue, clearErrors]);
  const hint = matchedOnPermit
    ? t('vehicles.permitAdd.plateOnPermit')
    : matchedOption
      ? t('vehicles.permitAdd.plateExisting')
      : typedKey && registry.isSuccess
        ? t('vehicles.permitAdd.plateNew')
        : '';

  const mutation = useMutation({
    mutationFn: async (values: Parsed) => {
      const items: VehicleWriteRequest[] = options
        .filter((v) => selected.has(v.uuid))
        .map((v) => ({ plate: v.plate })); // açıklama kayıttan; yeniden kullanımda yok sayılır
      if (values.plate.length > 0) {
        items.push({
          plate: values.plate,
          description:
            descriptionVisible && values.description.length > 0 ? values.description : null,
        });
      }
      await linkVehiclesSequentially(items, (body) => client.linkToPermit(permitUuid, body));
    },
    onSuccess: () => {
      reset();
      setSelected(new Set());
      onOpenChange(false);
    },
    onError: (error, values) => {
      if (!(error instanceof VehicleLinkBatchError)) {
        return;
      }
      // Bağlananlar işaretten düşer.
      setSelected((prev) => remainingSelection(prev, options, error.linkedPlates));
      // Düşen öğe elle girilen plakaysa sunucunun alan hataları forma bağlanır.
      if (error.failedPlate !== values.plate) {
        return;
      }
      for (const item of vehicleFieldErrors(error.original)) {
        setError(item.field, {
          type: 'server',
          message: vehicleFieldErrorMessage(item, error.original, t, values.description),
        });
      }
    },
    // Başarı da hata da: bağlananlar izin detayında + kayıt listesinde (yeni araç oluşmuş olabilir).
    onSettled: () => {
      inflightRef.current = false;
      client.invalidate();
    },
  });

  const submitting = mutation.isPending;
  const canSubmit = (selected.size > 0 || typedKey.length > 0) && !submitting;
  // Render sırasında okunur: RHF `formState` vekili yalnız render'da okunan alana abone olur.
  const dirty = selected.size > 0 || formState.isDirty;

  function closeNow() {
    mutation.reset();
    reset();
    setSelected(new Set());
    onOpenChange(false);
  }

  const { requestClose, discardDialog } = useDiscardGuard({
    dirty,
    busy: submitting,
    onClose: closeNow,
  });

  function toggle(uuid: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  }

  // Alana bağlanan sunucu hatası banner'a tekrarlanmaz — ama kısmi bağlanma bilgisi korunur.
  const batchError = mutation.error instanceof VehicleLinkBatchError ? mutation.error : null;
  const fullyMapped =
    batchError !== null && batchErrorFullyMapped(batchError, mutation.variables?.plate); // hata ANINDA forma bağlananlar; canlı form durumuna bakılmaz
  const showBanner = mutation.isError && !fullyMapped;

  const plateError = formState.errors.plate?.message;
  const descriptionError = formState.errors.description?.message;

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => !next && requestClose()}
        busy={submitting}
        tile
        title={t('vehicles.permitAdd.title')}
        description={t('vehicles.permitAdd.description')}
        closeLabel={t('common.close')}
        restoreFocusRef={restoreFocusRef}
        footer={
          <>
            <Button variant="secondary" onClick={requestClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="permit-vehicle-add-form"
              loading={submitting}
              disabled={!canSubmit}
            >
              {t('vehicles.permitAdd.submit')}
            </Button>
          </>
        }
      >
        <form
          id="permit-vehicle-add-form"
          className="flex flex-col gap-4"
          noValidate
          onSubmit={handleSubmit((values) => {
            // Eşzamanlı çift Enter: RHF doğrulaması async → senkron ref ile ikinci gönderimi kes.
            if (!canSubmit || inflightRef.current) {
              return;
            }
            inflightRef.current = true;
            mutation.mutate(values);
          })}
        >
          {showBanner ? (
            <Alert tone="danger">
              {batchError
                ? `${t('vehicles.permitAdd.partialFailure', { plate: batchError.failedPlate })} ${resolveApiErrorMessage(batchError.original, t)}`
                : resolveApiErrorMessage(mutation.error, t)}
            </Alert>
          ) : null}

          <FormSection legend={t('vehicles.permitAdd.registered')}>
            {registry.isLoading ? (
              <div className="flex min-h-24 items-center justify-center">
                <Spinner label={t('common.loading')} />
              </div>
            ) : registry.isError ? (
              <Alert tone="danger">{resolveApiErrorMessage(registry.error, t)}</Alert>
            ) : options.length === 0 ? (
              <p className="text-sm text-muted">{t('vehicles.permitAdd.registeredEmpty')}</p>
            ) : (
              <div className="flex max-h-72 flex-col gap-1 overflow-y-auto rounded-md border border-border p-2">
                {options.map((v) => (
                  <label
                    key={v.uuid}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-fg hover:bg-surface-muted"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0 accent-primary"
                      checked={selected.has(v.uuid)}
                      onChange={() => toggle(v.uuid)}
                      disabled={submitting}
                    />
                    <span className="min-w-0 break-words font-medium">{v.plate}</span>
                    {v.description ? (
                      <span className="min-w-0 truncate text-muted">· {v.description}</span>
                    ) : null}
                  </label>
                ))}
              </div>
            )}
          </FormSection>

          <FormSection legend={t('vehicles.permitAdd.newVehicle')}>
            <Field
              htmlFor="permit-vehicle-plate"
              label={t('vehicles.form.field.plate')}
              error={plateError}
            >
              <Input
                id="permit-vehicle-plate"
                autoComplete="off"
                invalid={Boolean(plateError)}
                aria-describedby={
                  plateError ? 'permit-vehicle-plate-error' : 'permit-vehicle-plate-hint'
                }
                placeholder={t('vehicles.form.platePlaceholder')}
                {...register('plate')}
              />
            </Field>
            {/* Canlı bölge erişilebilirlik ağacında HEP kalır (boşken sr-only). */}
            <p
              id="permit-vehicle-plate-hint"
              aria-live="polite"
              className={hint ? 'text-xs text-muted' : 'sr-only'}
            >
              {hint}
            </p>
            {descriptionVisible ? (
              <Field
                htmlFor="permit-vehicle-description"
                label={t('vehicles.form.field.description')}
                error={descriptionError}
              >
                <Textarea
                  id="permit-vehicle-description"
                  rows={3}
                  invalid={Boolean(descriptionError)}
                  aria-describedby={
                    descriptionError ? 'permit-vehicle-description-error' : undefined
                  }
                  {...register('description')}
                />
              </Field>
            ) : null}
          </FormSection>
        </form>
      </Sheet>

      {discardDialog}
    </>
  );
}
